import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, User, Ticket, Users, ArrowRight, CheckCircle } from 'lucide-react';

const Register = () => {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState(searchParams.get('role') || 'buyer');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name, role);
      toast.success('¡Cuenta creada exitosamente!');
      if (role === 'organizer') {
        navigate('/organizer');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la cuenta');
    } finally {
      setLoading(false);
    }
  };

  const organizerBenefits = [
    'Crea eventos ilimitados',
    'Panel de analytics en tiempo real',
    'Gestión de tickets y ventas',
    'Validación QR para control de acceso',
    'Reportes de ventas descargables',
    'Soporte técnico prioritario'
  ];

  return (
    <div className="min-h-screen pt-16 bg-muted/30">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Side - Info */}
          <div className="hidden lg:flex flex-col justify-center">
            <h1 className="text-4xl font-bold mb-4">
              {role === 'organizer' ? 'Empieza a vender tickets hoy' : 'Únete a MasterTicketHN.com'}
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              {role === 'organizer' 
                ? 'Crea tu cuenta de organizador y comienza a monetizar tus eventos de forma profesional.'
                : 'Compra tickets para los mejores eventos de Honduras de forma rápida y segura.'}
            </p>

            {role === 'organizer' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Beneficios para organizadores:</h3>
                {organizerBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    </div>
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            )}

            {role === 'buyer' && (
              <div className="glass-card p-6 mt-4">
                <h3 className="font-semibold mb-2">¿Organizas eventos?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Regístrate como organizador y empieza a vender tickets de tus eventos.
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => setRole('organizer')}
                  className="w-full"
                >
                  Quiero ser organizador
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>

          {/* Right Side - Form */}
          <div className="w-full max-w-md mx-auto lg:mx-0">
            <div className="glass-card p-8">
              {/* Logo */}
              <div className="text-center mb-6">
                <Link to="/" className="inline-flex items-center gap-2">
                  <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                    <span className="text-primary-foreground font-bold text-lg">MT</span>
                  </div>
                </Link>
                <h2 className="text-2xl font-bold mt-4">Crear Cuenta</h2>
                <p className="text-muted-foreground">
                  {role === 'organizer' ? 'Cuenta de Organizador' : 'Cuenta de Comprador'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Role Selection - Mobile */}
                <div className="lg:hidden space-y-2">
                  <Label>Tipo de cuenta</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setRole('buyer')}
                      className={`flex flex-col items-center justify-center rounded-xl border-2 p-4 transition-all ${
                        role === 'buyer' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-muted hover:border-primary/50'
                      }`}
                      data-testid="role-buyer"
                    >
                      <Ticket className={`w-6 h-6 mb-2 ${role === 'buyer' ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="text-sm font-medium">Comprador</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRole('organizer')}
                      className={`flex flex-col items-center justify-center rounded-xl border-2 p-4 transition-all ${
                        role === 'organizer' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-muted hover:border-primary/50'
                      }`}
                      data-testid="role-organizer"
                    >
                      <Users className={`w-6 h-6 mb-2 ${role === 'organizer' ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="text-sm font-medium">Organizador</span>
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Nombre completo</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Juan Pérez"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 h-12"
                      required
                      data-testid="name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12"
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Mínimo 6 caracteres"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 h-12"
                      required
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Repite tu contraseña"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 h-12"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full btn-primary h-12"
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? 'Creando cuenta...' : role === 'organizer' ? 'Crear Cuenta de Organizador' : 'Crear Cuenta'}
                </Button>
              </form>

              <p className="text-center text-sm text-muted-foreground mt-6">
                ¿Ya tienes cuenta?{' '}
                <Link to="/login" className="text-primary hover:underline font-medium">
                  Inicia sesión
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
